import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const Login = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(null); // 'no_user' or 'wrong_password'
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      login(response.data.access_token, response.data.user);
      toast.success(t('login.loginSuccess'));
      navigate('/bess/dashboard');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Login fehlgeschlagen';
      const status = error.response?.status;
      
      // Handle specific error cases based on backend responses
      if (status === 404 && errorMsg === 'user_not_found') {
        // User doesn't exist
        setModalType('no_user');
        setShowModal(true);
      } else if (status === 401 && errorMsg === 'wrong_password') {
        // Wrong password
        setModalType('wrong_password');
        setShowModal(true);
      } else if (status === 403 && errorMsg === 'email_not_verified') {
        // Email not verified
        setModalType('not_verified');
        setShowModal(true);
      } else {
        // Generic error
        toast.error(t('login.loginFailedGeneric'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    try {
      setLoading(true);
      await axios.post(`${API}/auth/request-password-reset`, { email });
      toast.success(t('login.resetEmailSent'));
      setShowModal(false);
    } catch (error) {
      // Handle rate limiting error
      if (error.response?.status === 429 && typeof error.response?.data?.detail === 'object') {
        const detail = error.response.data.detail;
        toast.error(detail.message || t('login.resetRateLimited'));
      } else {
        toast.error(error.response?.data?.detail || t('login.resetEmailError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      setLoading(true);
      await axios.post(`${API}/auth/resend-verification`, { email });
      toast.success(t('login.verificationResent'));
      setShowModal(false);
    } catch (error) {
      toast.error(t('login.resetEmailError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" data-testid="login-page">
      <div className="max-w-md w-full">
        <div className="card animate-fadeIn">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-slate-900 mb-2">{t('login.title')}</h2>
            <p className="text-slate-600">{t('login.subtitle')}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email">{t('login.email')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="ihre@email.de"
                data-testid="email-input"
              />
            </div>

            <div>
              <Label htmlFor="password">{t('login.password')}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                data-testid="password-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700 text-white"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? t('login.submitLoading') : t('login.submit')}
            </Button>
          </form>

          <div className="mt-6 space-y-2">
            <div className="text-center text-sm">
              <span className="text-slate-600">{t('login.noAccount')} </span>
              <Link to="/bess/register" className="text-green-600 hover:text-green-700 font-semibold" data-testid="register-link">
                {t('login.registerNow')}
              </Link>
            </div>
            <div className="text-center text-sm">
              <button
                type="button"
                onClick={() => {
                  if (email) {
                    setModalType('not_verified');
                    setShowModal(true);
                  } else {
                    toast.error(t('login.enterEmailFirst'));
                  }
                }}
                className="text-blue-600 hover:text-blue-700 font-semibold hover:underline"
              >
                {t('login.resendVerification')}
              </button>
            </div>
          </div>
        </div>

        {/* Modal for login errors */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 animate-fadeIn">
              {modalType === 'no_user' ? (
                <>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900">{t('login.noUserTitle')}</h3>
                    </div>
                  </div>
                  <p className="text-slate-600 mb-6">
                    {t('login.noUserText')}
                  </p>
                  <p className="text-slate-700 font-medium mb-6">
                    {t('login.noUserQuestion')}
                  </p>
                  <div className="flex gap-3">
                    <Button
                      onClick={() => setShowModal(false)}
                      variant="outline"
                      className="flex-1"
                    >
                      {t('common.cancel')}
                    </Button>
                    <Button
                      onClick={() => navigate('/bess/register')}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                    >
                      {t('login.registerNow')}
                    </Button>
                  </div>
                </>
              ) : modalType === 'wrong_password' ? (
                <>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900">{t('login.wrongPasswordTitle')}</h3>
                    </div>
                  </div>
                  <p className="text-slate-600 mb-6">
                    {t('login.wrongPasswordText')}
                  </p>
                  <p className="text-slate-700 font-medium mb-6">
                    {t('login.wrongPasswordQuestion')}
                  </p>
                  <div className="flex gap-3">
                    <Button
                      onClick={() => setShowModal(false)}
                      variant="outline"
                      className="flex-1"
                    >
                      {t('login.tryAgain')}
                    </Button>
                    <Button
                      onClick={handlePasswordReset}
                      className="flex-1 bg-blue-600 hover:bg-blue-700"
                      disabled={loading}
                    >
                      {loading ? t('login.sending') : t('login.resetPassword')}
                    </Button>
                  </div>
                </>
              ) : modalType === 'not_verified' ? (
                <>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900">{t('login.notVerifiedTitle')}</h3>
                    </div>
                  </div>
                  <p className="text-slate-600 mb-6">
                    {t('login.notVerifiedText')}
                  </p>
                  <p className="text-slate-700 font-medium mb-6">
                    {t('login.notVerifiedQuestion')}
                  </p>
                  <div className="flex gap-3">
                    <Button
                      onClick={() => setShowModal(false)}
                      variant="outline"
                      className="flex-1"
                    >
                      {t('common.cancel')}
                    </Button>
                    <Button
                      onClick={handleResendVerification}
                      className="flex-1 bg-blue-600 hover:bg-blue-700"
                      disabled={loading}
                    >
                      {loading ? t('login.sending') : t('login.sendLink')}
                    </Button>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
